export const HUBSPOT_LOADER_SCRIPT_ID = "hs-script-loader";
export const HUBSPOT_LOADER_FALLBACK_URL =
  "https://js-eu1.hs-scripts.com/26167171.js";
const HUBSPOT_CONVERSATIONS_EMBED_URL =
  "https://js-eu1.usemessages.com/conversations-embed.js";
const CONTROL_PROBE_PATH = "/manifest.json";
const PROBE_TIMEOUT_MS = 4000;

export type UrlProbeResult = "blocked" | "reachable" | "offline";

function isBrowserOffline(): boolean {
  return navigator.onLine === false;
}

/**
 * Reports whether `url` can leave the browser at all. A content blocker cancels
 * the request client-side, so an opaque no-cors fetch that rejects (or never
 * answers) means the request was filtered rather than refused by the server.
 */
export async function probeUrl(
  url: string,
  timeoutMs: number = PROBE_TIMEOUT_MS,
): Promise<UrlProbeResult> {
  if (isBrowserOffline()) return "offline";

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    await fetch(url, {
      method: "GET",
      mode: "no-cors",
      cache: "no-store",
      signal: controller.signal,
    });
    return "reachable";
  } catch {
    return isBrowserOffline() ? "offline" : "blocked";
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function anyUrlBlocked(
  urls: string[],
  timeoutMs: number = PROBE_TIMEOUT_MS,
): Promise<boolean> {
  const results = await Promise.all(
    urls.map((url) => probeUrl(url, timeoutMs)),
  );

  return results.includes("blocked");
}

/**
 * A failed probe on its own proves nothing — flaky wifi, a DNS hiccup and a
 * filtered request all look identical from here. Only when unrelated traffic
 * still gets through is the failure specific to `targetUrls`, which is what a
 * content blocker actually looks like. A server that is merely down answers
 * with a status code, so it resolves as reachable rather than landing here.
 */
export async function isBlockedRelativeToControl(
  targetUrls: string[],
  controlUrl: string,
  timeoutMs: number = PROBE_TIMEOUT_MS,
): Promise<boolean> {
  if (!(await anyUrlBlocked(targetUrls, timeoutMs))) return false;

  return (await probeUrl(controlUrl, timeoutMs)) === "reachable";
}

function getHubspotProbeUrls(): string[] {
  const loader = document.getElementById(HUBSPOT_LOADER_SCRIPT_ID);
  const loaderUrl =
    loader instanceof HTMLScriptElement && loader.src
      ? loader.src
      : HUBSPOT_LOADER_FALLBACK_URL;

  return [loaderUrl, HUBSPOT_CONVERSATIONS_EMBED_URL];
}

/**
 * Both URLs matter: blocklists that only carry the tracker domain still let the
 * loader through while killing the conversations widget it pulls in. Our own
 * manifest is the control — no blocklist carries it, so it reaching the network
 * while HubSpot cannot is the evidence that something is filtering HubSpot.
 */
export async function isHubspotBlockedByContentBlocker(): Promise<boolean> {
  return isBlockedRelativeToControl(
    getHubspotProbeUrls(),
    `${window.location.origin}${CONTROL_PROBE_PATH}`,
  );
}
